// Shared sell-side money-event form.  The backend remains the accounting authority:
// `cap` is read from the host's refreshed order/customer payload and is never derived here.
import React, { useRef, useState } from 'react';
import api from '../../services/api';
import { formatMoneyExact } from '../../utils/format';
import { DrawerActions } from '../../shell/DrawerBody';
import { RefusalBox } from '../../shell/drawer/OperationDrawer';
import { refusalOf } from '../../shell/drawer/refusal.mjs';
import { useTranslation } from 'react-i18next';
import useReturnReasons from './useReturnReasons';
import ReturnReasonFields from './ReturnReasonFields';
import { AccountPick } from '../FormPickers';
import AvoirOffer from './avoir/AvoirOffer';
import IssueNowCheck from './avoir/IssueNowCheck';   // the Return shortcut 'Also issue the credit note now'   // AVOIR Phase 4.2: refund voucher / return slip numbers and the offered draft credit note (only when the API sends them)
import './MoneyEventForm.css';
import EnumSelect from '../EnumSelect';
import { TextInput } from '../inputs/Input';
import MoneyTextInput from '../MoneyTextInput';
import { useAdminOnly } from './avoir/AvoirGate';
import { useCreditGate } from './CreditLimitNotice';   // E5 (GES-440): refund payouts / deposit refunds are credit doors
import { cents } from '../../pages/Sales/invoicing/invoicingModel.mjs';
import { newKey } from '../../lib/idempotencyKey.mjs';
import useDocFieldRules, { DocRequiredNote } from '../documents/useDocFieldRules';   // 47224 K-S10

export const MONEY_EVENT_TYPES = {
  deposit: {
    title: 'moneyEvent.spec.deposit.title',
    capLabel: 'moneyEvent.spec.deposit.capLabel',
    accountLabel: 'moneyEvent.spec.deposit.accountLabel',
    submitLabel: 'moneyEvent.spec.deposit.submitLabel',
  },
  return: {
    title: 'moneyEvent.spec.return.title',
    capLabel: 'moneyEvent.spec.return.capLabel',
    submitLabel: 'moneyEvent.spec.return.submitLabel',
  },
  'refund-payout': {
    title: 'moneyEvent.spec.refund_payout.title',
    capLabel: 'moneyEvent.spec.refund_payout.capLabel',
    accountLabel: 'moneyEvent.spec.refund_payout.accountLabel',
    submitLabel: 'moneyEvent.spec.refund_payout.submitLabel',
  },
  'deposit-refund': {
    title: 'moneyEvent.spec.deposit_refund.title',
    capLabel: 'moneyEvent.spec.deposit_refund.capLabel',
    accountLabel: 'moneyEvent.spec.deposit_refund.accountLabel',
    submitLabel: 'moneyEvent.spec.deposit_refund.submitLabel',
  },
  'customer-refund': {   // order-less payout of the customer's pool credit (customer page); cap = the customer's net credit (API snapshot refundable_credit)
    title: 'moneyEvent.spec.customer_refund.title',
    capLabel: 'moneyEvent.spec.customer_refund.capLabel',
    accountLabel: 'moneyEvent.spec.customer_refund.accountLabel',
    submitLabel: 'moneyEvent.spec.customer_refund.submitLabel',
  },
  'on-account': {
    title: 'moneyEvent.spec.on_account.title',
    capLabel: 'moneyEvent.spec.on_account.capLabel',
    submitLabel: 'moneyEvent.spec.on_account.submitLabel',
  },
};

const today = () => new Date().toISOString().slice(0, 10);
// GES-406 — up to 2 decimals (as before), separators + currency label from Settings ▸ Formatting.
const money = (value) => formatMoneyExact(String(value));   // 47001 W2: the served text as is, no float rounding
const AMOUNT_RE = /^\d{1,12}(\.\d{1,2})?$/;   // typed amount: exact decimal text, at most 2 decimals
const CAP_RE = /^\d+(\.\d+)?$/;
const bad = (message) => Object.assign(new Error(message), { isValidation: true });   // our own translated validation text (never a server message)
const saleIdOf = (order) => order?.sale_id || order?.saleId || order?.id || null;

// Kept exported so the browser form and the DEV endpoint-contract spec use the exact same wire shape.
// Do not add locally-derived amounts here: every cap is supplied by the server-backed host context.
export function buildMoneyEventRequest(type, { order, amount, accountId, date, notes, pool, customer, kind, invoiceLegId, reasonCode, reasonNote, idempotencyKey, issueCreditNote, destination }) {
  const common = { amount: String(amount).trim(), idempotency_key: idempotencyKey, date: date || null, notes: notes?.trim() || null };
  const saleId = saleIdOf(order);
  if (type === 'on-account') {
    return {
      endpoint: '/payment-allocations/apply-credit',
      body: {
        account_id: pool?.account_id || customer?.account_id || customer?.receivable_account_id || null,
        partner_id: customer?.id || customer?.partner_id || pool?.partner_id || null,
        kind,
        invoice_leg_id: invoiceLegId,
        amount: String(amount).trim(),
        idempotency_key: idempotencyKey,
      },
    };
  }
  if (type === 'customer-refund') {
    const pid = customer?.id || customer?.partner_id;
    if (!pid) throw new Error('A customer id is required for this money event.');
    return { endpoint: `/customers/${encodeURIComponent(pid)}/refund-payout`, body: { ...common, account_from_id: accountId, idempotency_key: idempotencyKey } };
  }
  const paths = {
    deposit: 'deposit',
    return: 'return',
    'refund-payout': 'refund-payout',
    'deposit-refund': 'deposit-refund',
  };
  if (!paths[type]) throw new Error(`Unknown money-event type: ${type}`);
  if (!saleId) throw new Error('A sale row id is required for this money event.');
  const body = { ...common };
  if (type === 'return' && issueCreditNote) { body.issue_credit_note = true; body.credit_note_date = date || null; }
  if (type === 'return' && reasonCode) { body.reason_code = reasonCode; if (reasonNote?.trim()) body.reason_note = reasonNote.trim(); }
  if (type === 'return' && (destination === 'money' || destination === 'credit')) body.destination = destination;   // engine: where the part already paid goes (customer money | credit); omitted = Settings default
  if (type === 'deposit') body.account_to_id = accountId;
  if (type === 'refund-payout' || type === 'deposit-refund') { body.account_from_id = accountId; }
  return { endpoint: `/sales/${encodeURIComponent(saleId)}/${paths[type]}`, body };
}

function readDerivedState(value) {
  const source = value?.header || value?.order || value?.customer || value || {};
  return {
    paymentStatus: source.payment_status ?? null,
    onAccountCredit: source.on_account_credit ?? null,
  };
}

// Props are deliberately data-only so Phase 3 can host this unmounted component in an order tab,
// global drawer, or an inline row. `cap` must be the server-provided ceiling for this exact action.
export default function MoneyEventForm({
  type,
  order = null,
  customer = null,
  cap = null,
  accounts = [],
  pool = null,
  targetOrders = [],
  embedded = false,
  hideTitle = false,   // a drawer that is already titled for this action does not repeat it
  onRefresh = null,
  onSuccess = () => {},
  onClose = () => {},
}) {
  const spec = MONEY_EVENT_TYPES[type];
  const adm = useAdminOnly(), payout = type === 'refund-payout' || type === 'deposit-refund' || type === 'customer-refund';   // block 1: every refund payout is admin only (the API answers REFUND_ADMIN_ONLY)
  const formId = `money-event-form-${type}`;   // the footer submit (fb-224 drawer) targets this form
  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState('');
  const [date, setDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState('');
  const [invoiceLegId, setInvoiceLegId] = useState('');
  const [error, setError] = useState(null);
  const credit = useCreditGate();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // State updates are asynchronous. This ref is the actual money-safety lock for two rapid submits.
  const inFlight = useRef(false);
  // one key per mounted form: a double submit / network retry of the same payout replays instead of paying twice (API moneyIdemKey)
  const idemKey = useRef(null);
  if (!idemKey.current) idemKey.current = newKey();
  const { t } = useTranslation('returnReasons');
  const { t: tm, i18n } = useTranslation('sales');
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [issueCn, setIssueCn] = useState(false);
  const [dest, setDest] = useState('');   // return only: '' = the Settings default (event_excess_destination)
  const reasons = useReturnReasons(type === 'return');   // db/730: required while at least one active reason exists
  // 47224: the document this event issues (refund voucher / return slip) and the rule fields AS THIS DOOR WRITES THEM: the voucher/slip doors store no notes
  // (rv_for_leg / rs_for_leg insert NULL) and this form sends no receiver / returner, so a rule requiring them blocks here with the engine's own text
  const docKind = payout ? 'refund_voucher' : type === 'return' ? 'return_slip' : null;
  const rules = useDocFieldRules(docKind, null);
  const missing = rules.missing(docKind === 'refund_voucher' ? { notes: '', received_by_name: '' } : docKind === 'return_slip' ? { notes: '', reason_code: reasonCode, returned_by: '', received_by: '' } : {});

  if (!spec) return <div role="alert">{tm('moneyEvent.unknownType')}</div>;
  const isOnAccount = type === 'on-account';
  const needsAccount = type === 'deposit' || type === 'refund-payout' || type === 'deposit-refund' || type === 'customer-refund';
  const fundingAccounts = (accounts || []).filter(a => !a.is_group && ['cash', 'bank'].includes(String(a.type || '').toLowerCase()));
  const credits = pool?.credits || [];
  const obligations = pool?.obligations || targetOrders || [];
  const hasCap = cap != null && CAP_RE.test(String(cap).trim());   // the server's served ceiling, kept as text

  const blocked = submitting || !hasCap || (payout && !adm.isAdmin) || missing.length > 0;   // a disabled primary names its reason on hover
  const blockedWhy = submitting ? tm('moneyEvent.processing') : !hasCap ? tm('moneyEvent.err.cap') : (payout && !adm.isAdmin && adm.reason) || rules.why(missing);

  async function submit(event, ov = null) {   // ov = the credit override fields merged into the SAME request
    event?.preventDefault();
    // Do this before validation and before React can repaint the disabled button.
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true); setError(null); credit.clear();
    try {
      const typed = String(amount).trim();
      if (!AMOUNT_RE.test(typed) || cents(typed) <= 0n) throw bad(tm('moneyEvent.err.amount'));
      if (!hasCap) throw bad(tm('moneyEvent.err.cap'));
      if (cents(typed) > cents(cap)) throw bad(tm('moneyEvent.err.exceeds', { amount: money(cap) }));
      if (type === 'return' && reasons.length && !reasonCode) throw bad(t('form.required'));
      if (needsAccount && !accountId) throw bad(tm('moneyEvent.err.account'));
      if (isOnAccount && !kind) throw bad(tm('moneyEvent.err.credit'));
      if (isOnAccount && !invoiceLegId) throw bad(tm('moneyEvent.err.target'));

      const request = buildMoneyEventRequest(type, {
        order, amount, accountId, date, notes, pool, customer, kind, invoiceLegId, reasonCode, reasonNote, idempotencyKey: idemKey.current, issueCreditNote: issueCn, destination: dest,
      });
      const response = await api.requestRaw(request.endpoint, { method: 'POST', body: JSON.stringify(ov ? { ...request.body, ...ov } : request.body) });
      if (!response.ok && credit.catchCredit(response.data, (o) => submit(null, o))) return;
      if (!response.ok) { setError(response.data && (response.data.code || response.data.error) ? refusalOf(response.data, i18n.language) : tm('moneyEvent.err.http', { status: response.status })); return; }
      // Phase 3 supplies the one authoritative re-read. We only display its derived fields.
      const refreshed = onRefresh ? await onRefresh({ type, order, customer, response: response.data }) : null;
      setResult({ response: response.data, derived: readDerivedState(refreshed) });
      onSuccess({ type, response: response.data, refreshed });
    } catch (e) {
      setError(e && e.isValidation ? e.message : tm('moneyEvent.err.failed'));
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  if (result) {
    const { paymentStatus, onAccountCredit } = result.derived;
    return (
      <div data-testid="money-event-success" className={embedded ? 'mef-card emb' : 'mef-card'}>
        <div className="mef-1">{tm('moneyEvent.recorded')}</div>
        {(paymentStatus != null || onAccountCredit != null) && (
          <div className="mef-2">
            {paymentStatus != null && <span data-testid="money-event-payment-status">{tm('moneyEvent.payStatus', { status: paymentStatus })}</span>}
            {paymentStatus != null && onAccountCredit != null && ' · '}
            {onAccountCredit != null && <span data-testid="money-event-on-account-credit">{tm('moneyEvent.onAcct', { amount: money(onAccountCredit) })}</span>}
          </div>
        )}
        <AvoirOffer data={result.response} onChanged={onRefresh ? () => onRefresh({ type, order, customer, response: result.response }) : undefined} />
        <button type="button" onClick={onClose} className="btn">{tm('moneyEvent.done')}</button>
      </div>
    );
  }

  return (
    <form id={formId} data-testid={`money-event-form-${type}`} onSubmit={submit} className={embedded ? 'mef-card emb' : 'mef-card'}>
      {!hideTitle && <div className="mef-4">{tm(spec.title)}</div>}
      <div data-testid="money-event-cap" className={`mef-cap${hasCap ? '' : ' warn'}`}>
        {tm(spec.capLabel)}: {hasCap ? `${money(cap)}` : tm('moneyEvent.unavailable')}
      </div>
      <div className="mef-5">
        <label className="mef-6">{tm('moneyEvent.amount')}
          <MoneyTextInput data-testid="money-event-amount" value={amount}
            onChange={e => setAmount(e.target.value)} className="mef-7" />
        </label>
        {needsAccount && <label className="mef-6">{tm(spec.accountLabel)}
          <AccountPick testid="money-event-account" accounts={fundingAccounts} labelFn={a => `${a.name} (${a.type})`} value={accountId} onChange={setAccountId} placeholder={tm('moneyEvent.acctOpt')} />
        </label>}
        {isOnAccount && <label className="mef-6">{tm('moneyEvent.pool')}
          <EnumSelect bare testid="money-event-credit" value={kind} onChange={v => setKind(v)}>
            <option value="">{tm('moneyEvent.creditOpt')}</option>
            {credits.map(c => <option key={c.kind} value={c.kind}>{c.kind} · {tm('moneyEvent.available', { amount: money(c.residual) })}</option>)}
          </EnumSelect>
        </label>}
        {isOnAccount && <label className="mef-6">{tm('moneyEvent.target')}
          <EnumSelect bare testid="money-event-target-order" value={invoiceLegId} onChange={v => setInvoiceLegId(v)}>
            <option value="">{tm('moneyEvent.orderOpt')}</option>
            {obligations.map(o => <option key={o.invoice_leg_id || o.id} value={o.invoice_leg_id || o.id}>{o.operation_id || o.order_id || tm('moneyEvent.order')} · {tm('moneyEvent.open', { amount: money(o.open ?? o.amount) })}</option>)}
          </EnumSelect>
        </label>}
        {type === 'return' && reasons.length > 0 && <ReturnReasonFields reasons={reasons} code={reasonCode} note={reasonNote}
          onCode={setReasonCode} onNote={setReasonNote} labelStyle={label} inputStyle={input} />}
        <label className="mef-6">{tm('moneyEvent.date')}
          <TextInput data-testid="money-event-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="mef-7" />
        </label>
        <label className="mef-6">{tm('moneyEvent.notes')}
          <TextInput data-testid="money-event-notes" type="text" value={notes} onChange={e => setNotes(e.target.value)} className="mef-7" />
        </label>
      </div>
      {type === 'return' && <div className="avf-field" data-testid="return-destination" style={{ marginBottom: 10 }}>
        <span className="avf-lab">{tm('moneyEvent.destination.label')}</span>
        <EnumSelect bare testid="return-destination-select" value={dest} onChange={setDest}>
          <option value="">{tm('moneyEvent.destination.default')}</option>
          <option value="money">{tm('moneyEvent.destination.money')}</option>
          <option value="credit">{tm('moneyEvent.destination.credit')}</option>
        </EnumSelect>
        <span className="sub">{tm('moneyEvent.destination.hint')}</span>
      </div>}
      {type === 'return' && <IssueNowCheck operationId={order?.operation_id} checked={issueCn} onChange={setIssueCn} />}
      {credit.notice}
      <DocRequiredNote missing={missing} />
      {error && (typeof error === 'string' ? <div role="alert" className="mef-8">{error}</div> : <RefusalBox refusal={error} testId="money-event-refusal" />)}
      <DrawerActions legacyRow="act-row mt12"
        legacyClose={<button type="button" onClick={onClose} disabled={submitting} className="btn">{tm('moneyEvent.cancel')}</button>}>
        {inFoot => (
          <button data-testid="money-event-submit" type="submit" form={formId} disabled={blocked} aria-disabled={blocked} title={blocked ? blockedWhy : undefined}
            className="btn success">
            {submitting ? tm('moneyEvent.processing') : tm(spec.submitLabel)}
          </button>
        )}
      </DrawerActions>
    </form>
  );
}

const label = { display: 'grid', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--muted)' };
const input = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', borderRadius: 4, padding: '6px 8px', background: 'var(--surface)', fontSize: 12 };

